import React, { useCallback } from "react";
import GenericBox from "components/ui/Box";
import { EyeIcon } from "components/icons/Icons";
import { Building2 } from "lucide-react";
import { Sizes } from "components/ui/Loader";
import { usePanel } from "hooks/usePanel";
import { NsKey } from "enums/NsKey";
import { useTranslation } from "react-i18next";
import { GetLegalProtectionRiskResponse } from "api/risk";
import { useRiskData, useSubscriptionProcessState } from "features/subscription-process/contexts/selectors/selectors";
import { LegalProtectionSummaryPanel } from "features/subscription-process/legal-protection/LegalProtectionSummaryPanel";
import { useRiskBeneficiaries } from "features/subscription-process/legal-protection/useRiskBeneficiaries";
import { withPersonName } from "shared/utils/panelTitle";
import { getPersonDisplayName, isLegalEntity } from "shared/utils/person";

/**
 * Company of a legal protection file in the pricing header, with the beneficiaries of the file
 * under its name. The button opens its read-only
 * summary (company, accounting data, subsidiaries, cumulated figures): nothing is edited from the
 * pricing page, the company step owns the edition and its eligibility check.
 */
export const InsuredLegalProtectionCard: React.FC = () => {
  const { t } = useTranslation(NsKey.Common);
  const { openPanel } = usePanel();
  const { riskId } = useSubscriptionProcessState();
  const riskData = useRiskData() as GetLegalProtectionRiskResponse | null;
  const person = riskData?.person;
  const name = person ? getPersonDisplayName(person) : '';
  const { beneficiaries } = useRiskBeneficiaries(riskId);

  const onSelectCard = useCallback(() => {
    if (person && riskId) {
      openPanel({
        title: withPersonName(t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUMMARY.TITLE"), isLegalEntity(person) ? name : undefined),
        size: Sizes["3xl"],
      },
        <LegalProtectionSummaryPanel company={person} riskId={riskId} />
      );
    }
  }, [person, name, openPanel, t, riskId]);

  return (
    <GenericBox
      icon={Building2}
      title={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.CARD_COMPANY")}
      subtitle={beneficiaries.length > 0
        ? (
          <span className="flex flex-col gap-1">
            <span>{name}</span>
            {beneficiaries.map(beneficiary => (
              <span key={beneficiary.personId} className="text-content">
                {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.BENEFICIARY", { name: beneficiary.displayName })}
              </span>
            ))}
          </span>
        )
        : name}
      onSelect={onSelectCard}
      actionIcon={EyeIcon}
    />
  );
};
